import axios from 'axios';

import { fetchAllEmployees } from 'root/apps/QuoteMachineIntegration/services/Employee';
import {
  MailingListPayload,
  MailingListType,
  PopulatedMailingList,
  PopulatedMailingListRecipient,
} from 'root/apps/QuoteMachineIntegration/state/MailingList';
import { getEmployeeEmail } from 'root/apps/QuoteMachineIntegration/utils/Employee';
import { formatRefId } from 'root/apps/QuoteMachineIntegration/utils/referenceHelpers';
import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../constants';

const V1_API_ROOT = `${LS_INVOICING_API_ROOT}/notifications`;
const NOTIFICATION_TYPE_ALL = 'ALL';

interface NotificationList {
  id: string;
  notificationType: string;
}

interface NotificationSubscriber {
  notificationRecipientRef: string;
}

type SubscribersBody = { subscribers: NotificationSubscriber[] };

interface SubscribersResponse {
  subscribers: NotificationSubscriber[];
  ignored?: string[];
}

function toPopulatedRecipients(
  employees: LSEmployee[],
  isSubscribed: (employeeId: string) => boolean
): PopulatedMailingListRecipient[] {
  return employees.map((employee) => ({
    employeeId: employee.employeeID,
    firstName: employee.firstName,
    lastName: employee.lastName,
    lsEmailAddress: getEmployeeEmail(employee),
    subscribed: isSubscribed(employee.employeeID),
  }));
}

async function upsertRecipient(employee: LSEmployee): Promise<void> {
  await axios.post(`${V1_API_ROOT}/recipients`, {
    notificationRecipientRef: formatRefId(employee.employeeID),
    firstName: employee.firstName,
    lastName: employee.lastName,
    emailAddress: getEmployeeEmail(employee),
  });
}

export function createMailingListService() {
  let cachedAllListId: string | null = null;
  let cachedSubscriberRefs: Set<string> | null = null;
  let cachedEmployees: LSEmployee[] | null = null;

  async function getAllListId(): Promise<string> {
    if (cachedAllListId) return cachedAllListId;
    const response = await axios.get<{ notificationLists: NotificationList[] }>(
      `${V1_API_ROOT}/lists`
    );
    const allList = response.data.notificationLists.find(
      (list) => list.notificationType === NOTIFICATION_TYPE_ALL
    );
    if (!allList) throw new Error('ALL notification list not found');
    cachedAllListId = allList.id;
    return cachedAllListId;
  }

  async function fetchSubscriberRefs(listId: string): Promise<Set<string>> {
    const { data } = await axios.get<SubscribersResponse>(
      `${V1_API_ROOT}/lists/${listId}/subscribers`
    );
    return new Set(data.subscribers.map((s) => s.notificationRecipientRef));
  }

  async function replaceSubscribers(listId: string, refs: string[]): Promise<void> {
    const url = `${V1_API_ROOT}/lists/${listId}/subscribers`;
    const body: SubscribersBody = {
      subscribers: refs.map((ref) => ({ notificationRecipientRef: ref })),
    };

    const ignored = (await axios.put<SubscribersResponse>(url, body))?.data?.ignored ?? [];
    if (!ignored.length) return;

    const employees = cachedEmployees ?? (await fetchAllEmployees());
    const ignoredRefs = new Set(ignored);
    const missing = employees.filter((e) => ignoredRefs.has(formatRefId(e.employeeID)));
    if (!missing.length) return;
    await Promise.all(missing.map(upsertRecipient));

    const stillIgnored = (await axios.put<SubscribersResponse>(url, body))?.data?.ignored ?? [];
    if (stillIgnored.length) {
      datadogRum.addError(
        new Error('LS_INVOICING_ERROR: Recipients still ignored after self-heal.'),
        { accountId: window.merchantos.account.id, ignored: stillIgnored }
      );
    }
  }

  async function fetchPopulatedNotificationList(): Promise<PopulatedMailingList> {
    const listId = await getAllListId();
    const [subscriberRefs, employees] = await Promise.all([
      fetchSubscriberRefs(listId),
      fetchAllEmployees(),
    ]);
    cachedSubscriberRefs = subscriberRefs;
    cachedEmployees = employees;

    return {
      id: listId,
      recipients: toPopulatedRecipients(employees, (id) => subscriberRefs.has(formatRefId(id))),
    };
  }

  async function createNotificationList(payload: MailingListPayload): Promise<void> {
    const listId = await getAllListId();
    const refs = payload.subscribe?.map((r) => formatRefId(r.employeeId)) ?? [];
    if (refs.length) {
      await replaceSubscribers(listId, refs);
      cachedSubscriberRefs = new Set(refs);
    }
  }

  async function updateNotificationList(payload: MailingListPayload): Promise<void> {
    const listId = await getAllListId();
    const currentRefs = cachedSubscriberRefs ?? (await fetchSubscriberRefs(listId));

    const desired = new Set(currentRefs);
    (payload.unsubscribe ?? []).forEach((r) => desired.delete(formatRefId(r.employeeId)));
    (payload.subscribe ?? []).forEach((r) => desired.add(formatRefId(r.employeeId)));

    if (desired.size === currentRefs.size && [...desired].every((ref) => currentRefs.has(ref))) {
      return;
    }

    await replaceSubscribers(listId, [...desired]);
    cachedSubscriberRefs = desired;
  }

  async function fetchPopulatedMailingList(
    listType: MailingListType
  ): Promise<PopulatedMailingList> {
    try {
      return await fetchPopulatedNotificationList();
    } catch (e) {
      datadogRum.addError(e, {
        message: `LS_INVOICING_ERROR: Could not fetch populated mailing list of type ${listType}.`,
        accountId: window.merchantos.account.id,
      });
      return { id: '', recipients: [] };
    }
  }

  async function createMailingList(listType: MailingListType, payload: MailingListPayload) {
    try {
      return await createNotificationList(payload);
    } catch (e) {
      datadogRum.addError(e, {
        errorType: 'LS_INVOICING_MAILING_LIST_ERROR',
        message: `Could not create mailing list of type ${listType}.`,
        accountId: window.merchantos.account.id,
        payload,
      });
      return [];
    }
  }

  async function updateMailingList(listType: MailingListType, payload: MailingListPayload) {
    try {
      return await updateNotificationList(payload);
    } catch (e) {
      datadogRum.addError(e, {
        errorType: 'LS_INVOICING_MAILING_LIST_ERROR',
        message: `Could not update mailing list of type ${listType}.`,
        accountId: window.merchantos.account.id,
        payload,
      });
      return [];
    }
  }

  return { fetchPopulatedMailingList, createMailingList, updateMailingList };
}

const defaultService = createMailingListService();
export const { fetchPopulatedMailingList, createMailingList, updateMailingList } = defaultService;
